import axios from 'axios';
import get from 'lodash/get';

import { urls } from '~/preloaded';
import settings from '~/settings';
import WssService, { WSS_ACTIONS } from '~/services/WssService';
import { searchToObject } from '~/utils/index';
import { safeJsonParse } from '~/utils/json';
import { sendCountingCWarsAttempts } from '~/web2ClientAPI/base';

import { actionsClanWars } from '~/Actions/ActionClanWars';
import { CLAN_WARS_STAGE } from '~/Reducers/ReducerClanWars';

import type { ISocketError, ISocketResponse } from '~/services/WssService';
import type { AppThunk } from '~/store';
import type { Ship } from '~/types/declaration';

const UPDATE_STAGES_AFTER_ADDITIONAL_ATTEMPTS_DELAY = 7500;
const UPDATE_STAGES_AFTER_WAR_STARTS_DELAY = 5000;
const UPDATE_LAST_ROUND_AFTER_PREPARATION_STARTS_DELAY = 5000;

export const loadHistoryModalDataThunk = (): AppThunk => (dispatch) => {
  WssService.getRoundsResultsHistory();
  dispatch(actionsClanWars.setHistoryIsFetching(true));
};

export const hideLastRoundResultsThunk = (): AppThunk => (dispatch, getState) => {
  const currentRound = get(getState(), ['ReducerClanWars', 'warSettings', 'currentRound'], 0);
  localStorage.setItem(`clanWarsRound_${currentRound}`, JSON.stringify({ lastRoundResultIsShows: true }));
  dispatch(actionsClanWars.hideLastRoundResults());
};

export const clearSearchThunk = (): AppThunk => (dispatch) => {
  dispatch(actionsClanWars.clearSearch());
  dispatch(getRatingsThunk());
};

export const getRatingsThunk =
  (clanId?: number, league?: number): AppThunk =>
  (dispatch, getState) => {
    const state = getState();
    const currentLeague = league || state.ReducerClanWars.selectedClanRatingLeague || undefined;
    dispatch(actionsClanWars.getRating());
    dispatch(actionsClanWars.setSelectedClanId(clanId));
    WssService.getRatings(clanId, currentLeague);
  };

export const initClanWarsThunk =
  (isReload: boolean): AppThunk =>
  (dispatch, getState) => {
    if (!settings.cwars.isEnabled) {
      return;
    }

    const state = getState();

    dispatch(checkReadyThunk());
    dispatch(actionsClanWars.init());
    dispatch(getShipsThunk());
    dispatch(checkFromParamThunk());

    const onMessage = (e: MessageEvent<string>) => {
      const data = safeJsonParse<ISocketResponse<Record<string, unknown>>>(e.data);
      if (!data) {
        return;
      }
      dispatch(actionsClanWars.setWssData(data.actionType, data.payload));

      const onWarStarts =
        data.actionType === WSS_ACTIONS.CWARS_STAGE_START && data.payload.currentStage === CLAN_WARS_STAGE.WAR;
      const onPreparationStarts =
        data.actionType === WSS_ACTIONS.CWARS_STAGE_START && data.payload.currentStage === CLAN_WARS_STAGE.PREPARATION;
      const onUpdateStagesInPreparation =
        data.actionType === WSS_ACTIONS.CWARS_UPDATE_STAGES &&
        (data.payload.currentStage === CLAN_WARS_STAGE.PREPARATION ||
          data.payload.currentStage === CLAN_WARS_STAGE.LULL);
      const onUpdateAdditionalAttempts = data.actionType === WSS_ACTIONS.CWARS_UPDATE_ADDITIONAL_ATTEMPTS;

      if (onUpdateStagesInPreparation && state.currentAccount.clanId) {
        WssService.getCommonInfo();
        WssService.getLastRoundResults(state.currentAccount.clanId);
      }

      if (onUpdateAdditionalAttempts) {
        // Random timeout to pass peak load
        setTimeout(() => WssService.getStagesInfo(), Math.random() * UPDATE_STAGES_AFTER_ADDITIONAL_ATTEMPTS_DELAY);
      }

      if (onWarStarts) {
        // Random timeout to pass peak load
        setTimeout(() => WssService.getStagesInfo(), Math.random() * UPDATE_STAGES_AFTER_WAR_STARTS_DELAY);
      }

      if (onPreparationStarts) {
        // Random timeout to pass peak load
        setTimeout(() => {
          if (state.currentAccount.clanId) {
            WssService.getLastRoundResults(state.currentAccount.clanId);
          }
        }, Math.random() * UPDATE_LAST_ROUND_AFTER_PREPARATION_STARTS_DELAY);
      }
    };

    const onError = (err: ISocketError) => {
      console.log(`*** ERROR ${err.request.actionType} ***`, err);
      dispatch(actionsClanWars.setFetching(false));
      dispatch(actionsClanWars.setError(err));
    };

    const onOpen = () => {
      dispatch(actionsClanWars.setOnline());
      WssService.getInitData();
    };

    const onClose = () => {
      dispatch(actionsClanWars.setOffline());
    };

    const onFailConnection = () => {
      dispatch(actionsClanWars.setFetching(false));
      dispatch(actionsClanWars.setError(new Error()));
    };

    WssService.init({
      onMessage: onMessage,
      onOpen: onOpen,
      onClose: onClose,
      onError: onError,
      onFailConnection: onFailConnection,
      url: urls.ws,
    });

    if (!state.ReducerClanWars.isReady) {
      dispatch(actionsClanWars.setFetching(true));
    }

    if (isReload) {
      WssService.getInitData();
    }
  };

const getShipsThunk = (): AppThunk => (dispatch) => {
  if (!urls.vehiclesInfo) {
    console.error('no vehiclesInfo url');
    return;
  }

  type IApiResponse = Record<string, Ship>;

  void axios
    .get<IApiResponse>(urls.vehiclesInfo)
    .then((resp) => {
      dispatch(actionsClanWars.getShip(resp.data));
    })
    .catch((err) => {
      console.error(err);
    });
};

const checkFromParamThunk = (): AppThunk => (dispatch) => {
  const searchParams = searchToObject();
  const isFromPort = searchParams && searchParams.source === 'port';
  dispatch(actionsClanWars.checkFromParam(isFromPort));

  if (isFromPort) {
    setTimeout(() => {
      dispatch(actionsClanWars.checkFromParam(false));
    }, 3000);
  }
};

const checkReadyThunk = (): AppThunk => (dispatch, getState) => {
  const state = getState();

  const canSetReady =
    state.ReducerClanWars.commonInfoReady &&
    state.ReducerClanWars.stageReady &&
    state.ReducerClanWars.warSettingsReady &&
    state.ReducerClanWars.shipsLoaded;

  if (canSetReady) {
    dispatch(actionsClanWars.checkReady(true));
    dispatch(actionsClanWars.setFetching(false));
  } else {
    setTimeout(() => {
      dispatch(checkReadyThunk());
    }, 300);
  }
};

export const setUseAttemptsThunk =
  (isAttemptsCount: boolean): AppThunk =>
  (_, getState) => {
    const state = getState();
    if (state.currentAccount.id) {
      sendCountingCWarsAttempts(state.currentAccount.id, isAttemptsCount);
      WssService.toggleAttemtsCount(isAttemptsCount);
    }
  };

export const toggleAutoParticipationThunk = (): AppThunk => (_, getState) => {
  const state = getState();
  const joinRoundAutomatically = state.ReducerClanWars.commonInfo.clan.settings.joinRoundAutomatically;
  WssService.changeJoinRoundAutomatically(!joinRoundAutomatically);
};

export const setIsParticipantThunk =
  (value: boolean): AppThunk =>
  () => {
    WssService.changeIsParticipating(value);
    WssService.getStagesInfo();
  };

export const showOwnClanPositionThunk = (): AppThunk => (dispatch, getState) => {
  const state = getState();
  const clanId = state.currentAccount?.clanId || null;
  if (clanId) {
    dispatch(getRatingsThunk(clanId));
  }
};

export const showSeasonLeadersThunk = () => {
  WssService.getRatingLeaders();
};

export const loadHistoryThunk =
  (limit = 10, offset = 0): AppThunk =>
  (dispatch) => {
    WssService.getRoundsResultsHistory(limit, offset);
    dispatch(actionsClanWars.setHistoryIsFetching(true));
  };
